// Turn CC · 8 — Run Insights card
// Placed in DailyBriefDetail sidebar, ABOVE the Editorial Notes card.
// Mirrors the light-theme aesthetic of the DailyBriefDetail page.

import { Link } from "react-router-dom";
import { Lightbulb, Loader2, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchers, qk } from "@/lib/queryClient";


const BUCKET_META = {
  dedup_removed: {
    label: "Already covered elsewhere",
    dot: "#3B82F6",           // blue
  },
  rejected_out_of_window: {
    label: "Older than yesterday",
    dot: "#F59E0B",           // orange
  },
  rejected_non_english: {
    label: "Not in English",
    dot: "#EF4444",           // red
  },
  rejected_blocked_source: {
    label: "From a blocked source",
    dot: "#334155",           // dark slate
  },
  rejected_audience_test: {
    label: "Not relevant for senior executives",
    dot: "#FBBF24",           // amber
  },
  rejected_low_score: {
    label: "Not important enough",
    dot: "#B45309",           // dark amber
  },
};

// Same visual language as the rest of DailyBriefDetail (light navy).
const BORDER = "#E5E9F0";
const INK = "#0F1B2D";
const MUTED = "#5B6B82";


function Divider() {
  return <div style={{ height: 1, background: BORDER }} />;
}


function VolumeBlock({ collected, kept, removed, statsAvailable }) {
  const Row = ({ label, value }) => (
    <div className="flex items-baseline justify-between py-1.5">
      <span style={{ color: MUTED, fontSize: 13 }}>{label}</span>
      <span
        style={{ color: INK, fontSize: 15, fontWeight: 600 }}
        className="tabular-nums"
      >
        {typeof value === "number" ? value.toLocaleString("en-US") : value}
      </span>
    </div>
  );
  return (
    <div data-testid="insights-volume">
      <Row
        label="Collected today"
        value={statsAvailable ? collected : "—"}
      />
      <Row
        label="Kept in the brief"
        value={kept}
      />
      <Row
        label="Removed"
        value={statsAvailable ? removed : "—"}
      />
    </div>
  );
}


function BreakdownBlock({ breakdown }) {
  const rows = Object.entries(breakdown || {})
    .map(([k, v]) => ({ k, v: Number(v || 0), meta: BUCKET_META[k] }))
    .filter((r) => r.meta && r.v > 0)
    .sort((a, b) => b.v - a.v);
  if (!rows.length) return null;
  return (
    <div className="pt-4" data-testid="insights-breakdown">
      <div
        className="mono uppercase tracking-widest mb-2"
        style={{ color: MUTED, fontSize: 10, letterSpacing: "0.12em" }}
      >
        Why we removed them
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.k}
            className="flex items-center gap-2.5"
            data-testid={`insights-bucket-${r.k}`}
          >
            <span
              className="inline-block rounded-full shrink-0"
              style={{ width: 8, height: 8, background: r.meta.dot }}
            />
            <span
              className="flex-1 truncate"
              style={{ color: INK, fontSize: 13 }}
            >
              {r.meta.label}
            </span>
            <span
              className="tabular-nums"
              style={{ color: MUTED, fontSize: 13, fontWeight: 500 }}
            >
              {r.v.toLocaleString("en-US")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function SuggestionsBlock({ suggestions }) {
  if (!suggestions?.length) return null;
  return (
    <div className="pt-4" data-testid="insights-suggestions">
      <div
        className="mono uppercase tracking-widest mb-3"
        style={{ color: MUTED, fontSize: 10, letterSpacing: "0.12em" }}
      >
        Suggestions
      </div>
      <div className="space-y-3">
        {suggestions.map((s, i) => (
          <div
            key={`${s.kind}-${s.segment || s.percent || i}`}
            className="flex gap-2.5 items-start"
            data-testid={`insights-suggestion-${s.kind}${s.segment ? "-" + s.segment : ""}`}
          >
            <Lightbulb
              className="shrink-0 mt-0.5"
              style={{ color: "#0AB99A", width: 14, height: 14 }}
            />
            <div className="min-w-0 flex-1">
              <p
                style={{ color: INK, fontSize: 13, lineHeight: 1.5 }}
              >
                {s.message}
              </p>
              {s.action_route && s.action_label && (
                <Link
                  to={s.action_route}
                  className="inline-flex items-center gap-1 mt-1 text-[12px] font-medium hover:underline"
                  style={{ color: "#0AB99A" }}
                  data-testid={`insights-suggestion-cta-${s.kind}`}
                >
                  <ArrowUpRight className="w-3 h-3" />
                  {s.action_label}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function RunInsightsCard({ briefId }) {
  /*
   * Turn CC · 16 — Migrated to react-query. The `run-insights` payload
   * is stable per-brief, so 5 min staleTime + persist cache = instant
   * paint on every subsequent open of the same brief.
   */
  const { data, error } = useQuery({
    queryKey: qk.dailyBriefRunInsights(briefId),
    queryFn: () => fetchers.dailyBriefRunInsights(briefId),
    staleTime: 5 * 60_000,
    enabled: !!briefId,
    retry: 1,
  });

  if (error && !data) {
    // Non-blocking: keep the surface consistent, just render nothing.
    return null;
  }

  return (
    <aside
      className="p-5 rounded-lg mb-4"
      style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}
      data-testid="run-insights-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="mono uppercase tracking-widest"
          style={{
            color: MUTED,
            fontSize: 11,
            letterSpacing: "0.12em",
            fontWeight: 600,
          }}
        >
          Run Insights
        </div>
        {data && !data.stats_available && (
          <span
            className="mono"
            style={{ color: MUTED, fontSize: 10 }}
            title="Detailed run stats will be available for briefs generated from tomorrow onward."
          >
            legacy brief
          </span>
        )}
      </div>

      {!data ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Loader2 className="animate-spin" style={{ width: 14, height: 14, color: MUTED }} />
          <span className="mono text-[11px] uppercase tracking-widest" style={{ color: MUTED }}>
            Loading…
          </span>
        </div>
      ) : (
        <>
          <VolumeBlock
            collected={data.collected}
            kept={data.kept}
            removed={data.removed}
            statsAvailable={data.stats_available}
          />
          {data.stats_available && (
            <>
              <div className="my-3"><Divider /></div>
              <BreakdownBlock breakdown={data.breakdown} />
              {(data.suggestions || []).length > 0 && (
                <>
                  <div className="my-3"><Divider /></div>
                  <SuggestionsBlock suggestions={data.suggestions} />
                </>
              )}
            </>
          )}
        </>
      )}
    </aside>
  );
}
